import { useContext, useEffect, useRef } from 'react';
import { useToast } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import API_URL from '../../constants/environment';
import SessionContext from '../../contexts/SessionContext';
import {
    finishExtractionObservationJob,
    listExtractionObservationJobs,
    subscribeExtractionObservationJobs,
    type TrackedExtractionObservationJob,
} from '../../hooks/invoice/extractionObservationJobTracker';

const POLL_MS = 2000;
const NOTICE_MS = 150_000;
const GIVE_UP_MS = 10 * 60_000;

interface JobStatusResponse {
    jobId: string;
    invoiceId: string;
    status: 'processing' | 'succeeded' | 'failed';
    error?: { message?: string };
}

/**
 * Keeps extraction-hint saves alive after the user leaves the invoice.
 * Sonnet runs in a background worker; this only watches the job.
 */
const ExtractionObservationJobWatcher = () => {
    const toast = useToast();
    const queryClient = useQueryClient();
    const sessionContext = useContext(SessionContext);
    const sessionId = sessionContext?.sessionId ?? null;
    const shownToastIds = useRef<Set<string>>(new Set());
    const pollingIds = useRef<Set<string>>(new Set());
    const noticedIds = useRef<Set<string>>(new Set());

    useEffect(() => {
        if (!sessionId) return;

        const toastId = (job: TrackedExtractionObservationJob) => `extraction-observation-${job.jobId}`;

        const showProcessingToast = (job: TrackedExtractionObservationJob) => {
            const id = toastId(job);
            if (shownToastIds.current.has(id) || toast.isActive(id)) return;
            shownToastIds.current.add(id);
            toast({
                id,
                title: 'Saving extraction hint',
                description: 'PartBay is remembering this correction. You can leave this page.',
                status: 'info',
                duration: null,
                isClosable: true,
            });
        };

        const closeProcessingToast = (job: TrackedExtractionObservationJob) => {
            const id = toastId(job);
            if (toast.isActive(id)) toast.close(id);
            shownToastIds.current.delete(id);
        };

        const settle = async (
            job: TrackedExtractionObservationJob,
            result: { status: 'succeeded' | 'failed' | 'unknown'; errorMessage?: string },
        ) => {
            finishExtractionObservationJob(job.jobId);
            closeProcessingToast(job);
            if (result.status === 'succeeded') {
                void queryClient.invalidateQueries({ queryKey: ['/invoice/extraction-layout', job.invoiceId] });
                toast({
                    title: 'AI extraction hint saved',
                    description: 'Future invoices from this vendor will use this correction.',
                    status: 'success',
                    duration: 4500,
                    isClosable: true,
                });
                return;
            }
            if (result.status === 'unknown') {
                toast({
                    title: 'Still saving extraction hint',
                    description: 'Check the extraction hints on this vendor. A timeout does not mean the save failed.',
                    status: 'info',
                    duration: 8000,
                    isClosable: true,
                });
                return;
            }
            toast({
                title: 'Could not save hint',
                description: result.errorMessage ?? 'Something went wrong while analyzing the invoice.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        };

        const pollJob = async (job: TrackedExtractionObservationJob) => {
            const { data } = await axios.post<JobStatusResponse>(
                `${API_URL}/invoice/extraction-observation/status`,
                { jobId: job.jobId },
                { headers: { Authorization: sessionId } },
            );
            if (data.status === 'processing') {
                const elapsed = Date.now() - job.startedAt;
                if (elapsed >= GIVE_UP_MS) {
                    await settle(job, { status: 'unknown' });
                    return;
                }
                if (elapsed >= NOTICE_MS && !noticedIds.current.has(job.jobId)) {
                    noticedIds.current.add(job.jobId);
                    toast({
                        title: 'Still saving extraction hint',
                        description: 'You can keep using PartBay. This will update when the hint finishes saving.',
                        status: 'info',
                        duration: 8000,
                        isClosable: true,
                    });
                }
                return;
            }
            await settle(job, {
                status: data.status === 'succeeded' ? 'succeeded' : 'failed',
                errorMessage: data.error?.message,
            });
        };

        const tick = async () => {
            const current = listExtractionObservationJobs();
            current.forEach(showProcessingToast);
            await Promise.all(current.map(async (job) => {
                if (pollingIds.current.has(job.jobId)) return;
                pollingIds.current.add(job.jobId);
                try {
                    await pollJob(job);
                } catch (err) {
                    console.error('[EXTRACTION_OBSERVATION_POLL]', err);
                    if (Date.now() - job.startedAt >= GIVE_UP_MS) {
                        await settle(job, { status: 'unknown' });
                    }
                } finally {
                    pollingIds.current.delete(job.jobId);
                }
            }));
        };

        const unsubscribe = subscribeExtractionObservationJobs(() => {
            void tick();
        });
        void tick();
        const timer = window.setInterval(() => {
            void tick();
        }, POLL_MS);
        return () => {
            unsubscribe();
            window.clearInterval(timer);
        };
    }, [queryClient, sessionId, toast]);

    return null;
};

export default ExtractionObservationJobWatcher;
